import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BellIcon from './BellIcon';
import ChatIcon from './ChatIcon';

const POLL_INTERVAL_MS = 30000;

function describe(notification) {
  if (notification.type === 'trip_join') return `${notification.actor_name} is in for your trip`;
  if (notification.type === 'trip_expense') return `${notification.actor_name} added a trip expense`;
  if (notification.type === 'map_invite') return `${notification.actor_name} invited you to a waypoint map`;
  const action = notification.type === 'like' ? 'liked' : 'commented on';
  const thing = notification.target_type === 'personal_best' ? 'your personal best'
    : notification.target_type === 'tournament_entry' ? 'your tournament entry'
    : 'your catch';
  return `${notification.actor_name} ${action} ${thing}`;
}

export default function NotificationBell() {
  const { listNotifications, markNotificationRead, markAllNotificationsRead, getTournamentEntry } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [badgePulsing, setBadgePulsing] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  // null until the first fetch lands, so that initial unread count (e.g. from a previous
  // session) doesn't itself read as "a new one just arrived" and pulse on mount.
  const previousUnreadRef = useRef(null);

  async function refresh() {
    const data = await listNotifications();
    setNotifications(data);
  }

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  useEffect(() => {
    if (previousUnreadRef.current !== null && unreadCount > previousUnreadRef.current) {
      setBadgePulsing(true);
      const timeout = setTimeout(() => setBadgePulsing(false), 500);
      previousUnreadRef.current = unreadCount;
      return () => clearTimeout(timeout);
    }
    previousUnreadRef.current = unreadCount;
  }, [unreadCount]);

  function handleToggle() {
    setOpen((previous) => !previous);
  }

  async function handleSelect(notification) {
    setOpen(false);
    if (!notification.read) {
      setNotifications((previous) => previous.map((item) => (item.id === notification.id ? { ...item, read: true } : item)));
      await markNotificationRead(notification.id);
    }
    if (notification.target_type === 'trip') {
      navigate(`/trips/${notification.target_id}`);
      return;
    }
    if (notification.target_type === 'waypoint_map') {
      navigate('/waypoints');
      return;
    }
    const commentParam = notification.comment_id ? `&comment=${notification.comment_id}` : '';
    if (notification.target_type === 'tournament_entry') {
      const entry = await getTournamentEntry(notification.target_id);
      navigate(entry ? `/tournaments/${entry.tournament_id}?entry=${notification.target_id}${commentParam}` : '/tournaments');
      return;
    }
    const href = notification.target_type === 'personal_best'
      ? `/profile?best=${notification.target_id}${commentParam}`
      : `/fish-year?catch=${notification.target_id}${commentParam}`;
    navigate(href);
  }

  async function handleMarkAllRead() {
    setNotifications((previous) => previous.map((item) => ({ ...item, read: true })));
    await markAllNotificationsRead();
  }

  return <div className="notification-bell" data-tour="notification-bell" ref={containerRef}>
    <button type="button" className="notification-bell-trigger" onClick={handleToggle} aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`} aria-expanded={open}>
      <BellIcon />
      {unreadCount > 0 && <span className={`notification-badge ${badgePulsing ? 'is-pulsing' : ''}`}>{unreadCount > 9 ? '9+' : unreadCount}</span>}
    </button>
    {open && <div className="notification-panel">
      <div className="notification-panel-head">
        <strong>Notifications</strong>
        {unreadCount > 0 && <button type="button" className="notification-mark-all" onClick={handleMarkAllRead}>Mark all read</button>}
      </div>
      {notifications.length === 0 && <p className="month-empty">Nothing yet. Get out there and catch something.</p>}
      <div className="notification-list">
        {notifications.map((notification) => <button type="button" key={notification.id} className={`notification-row ${notification.read ? '' : 'is-unread'}`} onClick={() => handleSelect(notification)}>
          <span className="notification-icon">{notification.type === 'like' ? <span aria-hidden="true">♥</span> : notification.target_type === 'waypoint_map' ? <span aria-hidden="true">⌖</span> : notification.target_type === 'trip' ? <span aria-hidden="true">{notification.type === 'trip_expense' ? '$' : '✓'}</span> : <ChatIcon />}</span>
          <span className="notification-text"><strong>{describe(notification)}</strong>{notification.preview && <em>"{notification.preview}"</em>}</span>
        </button>)}
      </div>
    </div>}
  </div>;
}
